import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

import { enforceRateLimit } from "./rate-limit.server";

const SlugInput = z.object({ slug: z.string().trim().min(1).max(120) });
const BidInput = z.object({
  slug: z.string().trim().min(1).max(120),
  amount: z.number().int().positive().max(100_000_000),
});

export type PublicBid = {
  id: string;
  amount: number;
  createdAt: string;
  who: string;
  letter: string;
  isMine: boolean;
  isAuto: boolean;
};


export type AuctionState = {
  saleKind: string;
  status: string;
  startPrice: number;
  endsAt: string | null;
  bidCount: number;
  topBid: number | null;
  minNext: number;
  reserveMet: boolean;
  hasReserve: boolean;
  bids: PublicBid[];
};

/** A, B, C … Z, AA, AB – bokstavsetikett utifrån ordningsnummer (0-baserat). */
function letterFor(index: number): string {
  let n = index;
  let out = "";
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

/**
 * Budgivare visas alltid anonymt – "Budgivare A", "Budgivare B" och så vidare,
 * i den ordning de lade sitt första bud.
 */
function pseudonyms(rows: { bidder_id: string; created_at: string }[]): Map<string, string> {
  const first = new Map<string, string>();
  for (const r of rows) {
    const prev = first.get(r.bidder_id);
    if (!prev || r.created_at < prev) first.set(r.bidder_id, r.created_at);
  }
  const order = [...first.entries()].sort((a, b) => (a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0));
  return new Map(order.map(([id], i) => [id, letterFor(i)]));
}


/** Publik auktionsstatus för en annons – budhistorik, ledande bud och lägsta nästa bud. */
export const getAuctionState = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => SlugInput.parse(input))
  .handler(async ({ data }) => {
    // Budtabellen är låst i databasen. Den publika listan är anonymiserad och
    // sätts ihop här på servern.
    const { supabaseAdmin: supabase } = await import("@/integrations/supabase/client.server");

    const { data: ad } = await supabase
      .from("ads")
      .select(
        "id, sale_kind, auction_status, start_price, reserve_price, auction_ends_at, price, hidden, deleted_at",
      )
      .eq("slug", data.slug)
      .is("deleted_at", null)
      .eq("hidden", false)
      .maybeSingle();

    if (!ad || ad.sale_kind !== "auction") return null;

    const { data: rows } = await supabase
      .from("bids")
      .select("id, amount, created_at, bidder_id, is_auto")
      .eq("ad_id", ad.id)
      .is("withdrawn_at", null)
      .order("amount", { ascending: false })
      .limit(50);


    const bids = rows ?? [];
    const names = pseudonyms(bids);
    const topBid = bids.length ? bids[0].amount : null;
    const startPrice = ad.start_price ?? ad.price;
    const { bidIncrement } = await import("./auctions");

    const state: AuctionState = {
      saleKind: ad.sale_kind,
      status: ad.auction_status ?? "none",
      startPrice,
      endsAt: ad.auction_ends_at,
      bidCount: bids.length,
      topBid,
      minNext: topBid ? topBid + bidIncrement(topBid) : startPrice,
      reserveMet: ad.reserve_price ? (topBid ?? 0) >= ad.reserve_price : true,
      hasReserve: !!ad.reserve_price,
      bids: bids.map((b) => ({
        id: b.id,
        amount: b.amount,
        createdAt: b.created_at,
        who: `Budgivare ${names.get(b.bidder_id) ?? "?"}`,
        letter: names.get(b.bidder_id) ?? "?",
        isAuto: !!(b as { is_auto?: boolean }).is_auto,
        isMine: false,
      })),
    };
    return state;
  });

/** Lägger ett bud. All kontroll (budsteg, tid, egen annons) sker i databasen. */
export const placeBid = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => BidInput.parse(input))
  .handler(async ({ data, context }) => {
    await enforceRateLimit("bid", context.userId);
    const { readSettings } = await import("./orders.server");
    if (!(await readSettings()).auctionsEnabled) {
      throw new Error("Auktioner är inte igång just nu.");
    }

    const { data: ad, error: adError } = await context.supabase
      .from("ads")
      .select("id, title, user_id")
      .eq("slug", data.slug)
      .maybeSingle();
    if (adError || !ad) throw new Error("Annonsen hittades inte.");

    // Funktionen är serveronly – vi har redan verifierat vem som är inloggad.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin.rpc("place_bid", {
      _ad_id: ad.id,
      _amount: data.amount,
      _actor: context.userId,
    });
    if (error) throw new Error(error.message.replace(/^.*?:\s*/, ""));

    const result = Array.isArray(rows) ? rows[0] : rows;

    // Säljaren ska veta direkt att det kommit ett bud.
    try {
      const { notifySellerNewBid } = await import("./auctions.server");
      await notifySellerNewBid({
        sellerId: ad.user_id as string,
        slug: data.slug,
        title: ad.title as string,
        amount: data.amount,
      });
    } catch (err) {
      console.error("[auktion] kunde inte avisera säljaren", err);
    }

    return result as {
      bid_id: string;
      amount: number;
      min_next: number;
      ends_at: string;
      reserve_met: boolean;
    };
  });

/** Dina egna bud på en annons – används för att markera "Du" i budhistoriken. */
export const myBids = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => SlugInput.parse(input))
  .handler(async ({ data, context }) => {
    const { data: ad } = await context.supabase
      .from("ads")
      .select("id")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!ad) return [] as string[];
    const { data: rows } = await context.supabase
      .from("bids")
      .select("id")
      .eq("ad_id", ad.id)
      .eq("bidder_id", context.userId);
    return (rows ?? []).map((r) => r.id);
  });

/** Ditt hemliga maxbud på en annons (bara du ser det). */
export const myMaxBid = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => SlugInput.parse(input))
  .handler(async ({ data, context }) => {
    const { data: ad } = await context.supabase
      .from("ads")
      .select("id")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!ad) return { maxAmount: null as number | null };
    const { data: row } = await context.supabase
      .from("auction_max_bids")
      .select("max_amount")
      .eq("ad_id", ad.id)
      .eq("bidder_id", context.userId)
      .maybeSingle();
    return { maxAmount: (row?.max_amount as number | undefined) ?? null };
  });

/**
 * Sätter (eller höjer) ditt maxbud. Systemet budar automatiskt åt dig med
 * lägsta möjliga belopp så länge du har utrymme kvar.
 */
export const setMaxBid = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => BidInput.parse(input))
  .handler(async ({ data, context }) => {
    await enforceRateLimit("bid", context.userId);
    const { readSettings } = await import("./orders.server");
    if (!(await readSettings()).auctionsEnabled) {
      throw new Error("Auktioner är inte igång just nu.");
    }

    const { data: ad, error: adError } = await context.supabase
      .from("ads")
      .select("id, title, user_id")
      .eq("slug", data.slug)
      .maybeSingle();
    if (adError || !ad) throw new Error("Annonsen hittades inte.");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin.rpc("set_max_bid", {
      _ad_id: ad.id,
      _amount: data.amount,
      _actor: context.userId,
    });
    if (error) throw new Error(error.message.replace(/^.*?:\s*/, ""));

    const result = (Array.isArray(rows) ? rows[0] : rows) as {
      max_amount: number;
      top_amount: number | null;
      ends_at: string;
      min_next: number;
      is_leading: boolean;
      reserve_met: boolean;
    };

    // Ledde maxbudet till ett automatiskt bud får säljaren veta det.
    if (result?.is_leading && result.top_amount) {
      try {
        const { notifySellerNewBid } = await import("./auctions.server");
        await notifySellerNewBid({
          sellerId: ad.user_id as string,
          slug: data.slug,
          title: ad.title as string,
          amount: Number(result.top_amount),
        });
      } catch (err) {
        console.error("[auktion] kunde inte avisera säljaren", err);
      }
    }

    return result;
  });


const SlugsInput = z.object({ slugs: z.array(z.string().trim().min(1).max(120)).max(60) });

export type TopBid = { slug: string; amount: number; count: number };

/** Ledande bud för flera auktionsannonser på en gång – används i annonslistorna. */
export const topBidsForSlugs = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => SlugsInput.parse(input))
  .handler(async ({ data }) => {
    if (!data.slugs.length) return [] as TopBid[];
    const { supabaseAdmin: supabase } = await import("@/integrations/supabase/client.server");
    const { data: ads } = await supabase
      .from("ads")
      .select("id, slug")
      .in("slug", data.slugs)
      .eq("sale_kind", "auction");
    const rows = ads ?? [];
    if (!rows.length) return [] as TopBid[];

    const { data: bids } = await supabase
      .from("bids")
      .select("ad_id, amount")
      .is("withdrawn_at", null)
      .in(
        "ad_id",
        rows.map((a) => a.id),
      );


    const byAd = new Map<string, { amount: number; count: number }>();
    for (const b of bids ?? []) {
      const cur = byAd.get(b.ad_id) ?? { amount: 0, count: 0 };
      byAd.set(b.ad_id, { amount: Math.max(cur.amount, b.amount), count: cur.count + 1 });
    }
    return rows
      .map((a) => {
        const agg = byAd.get(a.id);
        return agg ? { slug: a.slug, amount: agg.amount, count: agg.count } : null;
      })
      .filter(Boolean) as TopBid[];
  });

export type AltBid = { id: string; amount: number; createdAt: string; who: string; letter: string };

export type AuctionOutcome = {
  role: "winner" | "seller" | "none";
  amount: number | null;
  otherName: string | null;
  sold: boolean;
  /** Säljaren kan gå vidare till ett lägre bud inom 30 dagar. */
  canReassign: boolean;
  alternatives: AltBid[];
};

/** Vad hände med auktionen för just mig – vann jag, sålde jag, eller inget av det? */
export const myAuctionOutcome = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => SlugInput.parse(input))
  .handler(async ({ data, context }) => {
    const none: AuctionOutcome = {
      role: "none",
      amount: null,
      otherName: null,
      sold: false,
      canReassign: false,
      alternatives: [],
    };
    const { data: ad } = await context.supabase
      .from("ads")
      .select("id, user_id, sale_kind, auction_status, winning_bid_id, sold_at, reserve_price")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!ad || ad.sale_kind !== "auction" || !ad.winning_bid_id) return none;

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: bid } = await supabaseAdmin
      .from("bids")
      .select("amount, bidder_id")
      .eq("id", ad.winning_bid_id)
      .maybeSingle();
    if (!bid) return none;

    const isSeller = ad.user_id === context.userId;
    const isWinner = bid.bidder_id === context.userId;
    if (!isSeller && !isWinner) return { ...none, sold: ad.auction_status === "sold" };

    const otherId = isSeller ? bid.bidder_id : (ad.user_id as string);
    const { data: profile } = await context.supabase
      .from("profiles")
      .select("display_name")
      .eq("user_id", otherId)
      .maybeSingle();

    let alternatives: AltBid[] = [];
    const fresh =
      !!ad.sold_at && Date.now() - new Date(ad.sold_at).getTime() < 30 * 24 * 60 * 60 * 1000;

    if (isSeller && fresh) {
      const { data: rows } = await context.supabase
        .from("bids")
        .select("id, amount, created_at, bidder_id")
        .eq("ad_id", ad.id)
        .is("withdrawn_at", null)
        .order("amount", { ascending: false })
        .limit(50);
      const all = rows ?? [];
      const names = pseudonyms(all);
      alternatives = all
        .filter((b) => b.id !== ad.winning_bid_id && (!ad.reserve_price || b.amount >= ad.reserve_price))
        .map((b) => ({
          id: b.id,
          amount: b.amount,
          createdAt: b.created_at,
          letter: names.get(b.bidder_id) ?? "?",
          who: `Budgivare ${names.get(b.bidder_id) ?? "?"}`,
        }));
    }

    return {
      role: isSeller ? "seller" : "winner",
      amount: bid.amount,
      otherName: profile?.display_name ?? null,
      sold: true,
      canReassign: isSeller && fresh && alternatives.length > 0,
      alternatives,
    } satisfies AuctionOutcome;
  });

const AwardInput = z.object({
  slug: z.string().trim().min(1).max(120),
  bidId: z.string().uuid(),
});

/**
 * Säljaren går vidare till nästa budgivare när affären inte blev av.
 * Det tidigare vinnande budet stryks och den nya vinnaren får notis, tråd och mejl.
 */
export const awardNextBidder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => AwardInput.parse(input))
  .handler(async ({ data, context }) => {
    const { data: ad, error: adError } = await context.supabase
      .from("ads")
      .select("id, title, user_id")
      .eq("slug", data.slug)
      .maybeSingle();
    if (adError || !ad) throw new Error("Annonsen hittades inte.");
    if (ad.user_id !== context.userId) throw new Error("Bara säljaren kan välja en annan vinnare.");

    // Funktionen är serveronly – ägarskapet är redan kontrollerat ovan.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin.rpc("award_next_bidder", {
      _ad_id: ad.id,
      _bid_id: data.bidId,
      _actor: context.userId,
    });
    if (error) throw new Error(error.message.replace(/^.*?:\s*/, ""));

    const result = (Array.isArray(rows) ? rows[0] : rows) as {
      winner_id: string;
      amount: number;
      previous_winner_id: string;
      previous_amount: number;
    };

    const { announceNewWinner } = await import("./auctions.server");
    await announceNewWinner({
      adId: ad.id as string,
      slug: data.slug,
      title: ad.title as string,
      sellerId: context.userId,
      winnerId: result.winner_id,
      amount: Number(result.amount),
      previousWinnerId: result.previous_winner_id,
    });

    return result;
  });


export type BidActivityRow = {
  slug: string;
  title: string;
  imageUrl: string | null;
  endsAt: string | null;
  auctionStatus: string;
  myBid: number;
  topBid: number;
  bidCount: number;
  status: "leading" | "outbid" | "won" | "lost" | "withdrawn";
  sellerName: string | null;
};

/** Allt jag budat på – pågående, vunna och avslutade auktioner. */
export const myBidActivity = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: mine } = await context.supabase
      .from("bids")
      .select("id, ad_id, amount, withdrawn_at")
      .eq("bidder_id", context.userId);

    const rows = mine ?? [];
    if (!rows.length) return [] as BidActivityRow[];

    const adIds = [...new Set(rows.map((b) => b.ad_id))];
    const { data: ads } = await context.supabase
      .from("ads")
      .select(
        "id, slug, title, image_url, auction_ends_at, auction_status, winning_bid_id, user_id",
      )
      .in("id", adIds);

    // Andras bud är låsta i databasen – de sammanställs på servern och
    // returneras bara som ledande belopp och antal, aldrig med budgivarens identitet.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: all } = await supabaseAdmin
      .from("bids")
      .select("ad_id, amount, bidder_id, withdrawn_at")
      .in("ad_id", adIds);


    const sellerIds = [...new Set((ads ?? []).map((a) => a.user_id).filter(Boolean))] as string[];
    const { data: profiles } = sellerIds.length
      ? await context.supabase
          .from("profiles")
          .select("user_id, display_name")
          .in("user_id", sellerIds)
      : { data: [] as { user_id: string; display_name: string }[] };
    const nameById = new Map((profiles ?? []).map((p) => [p.user_id, p.display_name]));

    // Ledande bud och antal bud per annons – strukna bud räknas inte.
    const agg = new Map<string, { top: number; count: number }>();
    for (const b of all ?? []) {
      if (b.withdrawn_at) continue;
      const cur = agg.get(b.ad_id) ?? { top: 0, count: 0 };
      agg.set(b.ad_id, { top: Math.max(cur.top, b.amount), count: cur.count + 1 });
    }

    // Vann jag? Kolla vinnande budet direkt.
    const winningIds = (ads ?? []).map((a) => a.winning_bid_id).filter(Boolean) as string[];
    const { data: winners } = winningIds.length
      ? await supabaseAdmin.from("bids").select("id, bidder_id").in("id", winningIds)
      : { data: [] as { id: string; bidder_id: string }[] };
    const winnerByBidId = new Map((winners ?? []).map((w) => [w.id, w.bidder_id]));

    const out: BidActivityRow[] = [];
    for (const ad of ads ?? []) {
      const my = rows.filter((b) => b.ad_id === ad.id);
      const live = my.filter((b) => !b.withdrawn_at);
      const myBid = live.length
        ? Math.max(...live.map((b) => b.amount))
        : Math.max(...my.map((b) => b.amount));
      const a = agg.get(ad.id) ?? { top: myBid, count: live.length };
      const ongoing = ad.auction_status === "live";
      const iWon = ad.winning_bid_id
        ? winnerByBidId.get(ad.winning_bid_id) === context.userId
        : false;

      const status: BidActivityRow["status"] = ongoing
        ? a.top === myBid && live.length
          ? "leading"
          : "outbid"
        : iWon
          ? "won"
          : !live.length && my.length
            ? "withdrawn"
            : "lost";

      out.push({
        slug: ad.slug,
        title: ad.title,
        imageUrl: ad.image_url ?? null,
        endsAt: ad.auction_ends_at,
        auctionStatus: ad.auction_status ?? "none",
        myBid,
        topBid: a.top,
        bidCount: a.count,
        status,
        sellerName: nameById.get(ad.user_id as string) ?? null,
      });
    }

    return out.sort((x, y) => {
      const xe = x.endsAt ? new Date(x.endsAt).getTime() : Infinity;
      const ye = y.endsAt ? new Date(y.endsAt).getTime() : Infinity;
      return xe - ye;
    });
  });
